/**
 * skillReviewVocabulary.js — the words for the external-Skill review surface.
 *
 * The surface has to display seven closed sets that the SERVER owns: the two review states, the two
 * admission verdicts, the four §3 stages, the six source forms, the sixteen rejection reasons, the
 * three activation statuses that are out of scope, and the nine authorities a review does not carry.
 * This module holds the Hebrew for each arm and nothing else — no option list, no order, no default.
 *
 * That split is the point, and it is the same one `ticketStatusVocabulary` makes. The option lists,
 * the order of the reasons in §3's precedence, and which arms are still live all come from
 * `GET /api/tech-scout/skill-review/contract` at render time. A client that hardcoded them would
 * keep offering an arm the pipeline has renamed — and for THIS vocabulary the stale arm would be a
 * rejection reason, so a renamed refusal would render as a blank cell next to a candidate the
 * operator is being asked to trust.
 *
 * What this module must never acquire is a word for something that is not a fact of the review. The
 * review grants nothing (§3: `Skill != Capability != Tool != Connection != Runtime`), so there is no
 * arm here for installing, enabling, approving, adopting or executing, and the authority sentences
 * below are all statements that the review does NOT do. `skillReviewVocabulary.parity.test.js` reads
 * the two backend modules as text and pins every key set in both directions, so an arm added or
 * renamed on the server reds this module rather than silently printing a Hebrew word for it.
 */

/** Every resolver's answer for a value outside its scale, and for anything that is not a value. */
export const SKILL_REVIEW_UNKNOWN_HE = "לא מזוהה";

// ─── The two review states ─────────────────────────────────────────────────────
//
// "the gate answered" against "the gate could not be asked". Only the first is a verdict, so the two
// arms are worded as different KINDS of answer rather than as good and bad: `declaration_incomplete`
// is not a rejection and must not read like one.

export const SKILL_REVIEW_STATE_HE = {
  reviewed: "נבדק — התקבלה הכרעה",
  declaration_incomplete: "ההצהרה חסרה — לא התקבלה הכרעה",
};

// ─── The two admission verdicts ────────────────────────────────────────────────

export const SKILL_REVIEW_VERDICT_HE = {
  // §3's "admitted" is admission to the EVALUATING arrow, not to the product. The word says so,
  // because "אושר" alone on this card would read as an approval to use the thing.
  admitted: "אושר למעבר להערכה",
  rejected: "נדחה",
};

// ─── The four §3 stages ────────────────────────────────────────────────────────

export const SKILL_REVIEW_STAGE_HE = {
  discovered: "התגלה",
  candidate: "מועמד",
  security_review: "סקירת אבטחה",
  evaluating: "בהערכה",
};

// ─── The six source forms (§3's origin list) ───────────────────────────────────

export const SKILL_REVIEW_SOURCE_FORM_HE = {
  internal_approved: "מיומנות פנימית מאושרת",
  repository: "מאגר קוד",
  catalog: "קטלוג",
  framework: "פריימוורק",
  vendor: "ספק",
  community: "קהילה",
};

// ─── The sixteen rejection reasons, one word per arm ───────────────────────────
//
// Keyed by token only. The ORDER these are shown in is the server's `rejection_precedence`, which is
// a §3 fact ("the named reason is the highest-precedence finding, not the first one noticed") and not
// something a client may decide. `skillReviewReasonLabels` therefore preserves its input order.

export const SKILL_REVIEW_REASON_HE = {
  malformed_entry: "רשומה פגומה — חסר מזהה או שם",
  unsupported_domain: "תחום שאין למערכת מפתח עבורו",
  self_attested_source: "המקור אישר את עצמו",
  untrusted_source: "לא הוצג אישור מקור",
  source_attestation_mismatch: "האישור אינו תואם את פרטי המקור",
  source_revision_mismatch: "האישור אינו מוצמד למהדורה הנבדקת",
  source_attestation_revoked: "האישור בוטל",
  source_attestation_expired: "האישור פג תוקף",
  source_attestation_after_review: "האישור הונפק אחרי מועד הבדיקה",
  secret_in_candidate: "נמצא סוד בגוף המועמד",
  malformed_claim: "שדה הצהרה פגום",
  unpinned_candidate: "לא הוצמד לקומיט או לתקציר תוכן",
  unsigned_candidate: "אין חתימת מפרסם",
  self_signed_candidate: "נחתם בידי מי שגילה אותו",
  unsupported_tool_reference: "הפניה לכלי שאינה מזהה מבוקר-מדיניות",
  incomplete_candidate: "חסרים שדות החובה של §3",
};

// ─── §3's LAST arrow: the three statuses this review does not reach ────────────

export const SKILL_REVIEW_OUT_OF_SCOPE_HE = {
  active: "פעילה",
  suspended: "מושהית",
  deprecated: "מיושנת",
};

/**
 * The sentence that has to be printed NEXT TO the three statuses above, because the statuses
 * themselves are §3 arrow names a reader may misread as reachable from this screen.
 */
export const SKILL_REVIEW_OUT_OF_SCOPE_NOTE_HE =
  "מצבי הרישום האלה אינם נקבעים במסך הזה: מיומנות מגיעה אליהם רק דרך שער הרישום שאישר אדם.";

// ─── The nine authorities a review does not carry ──────────────────────────────
//
// Keyed by the server's `authority` block, every value of which is False. Each sentence is the
// negative of one key, so the card can print what the review did NOT do without the surface
// asserting it in its own words — the block travels on the wire and this module only words it.

export const SKILL_REVIEW_AUTHORITY_HE = {
  writes_anything: "הבדיקה אינה כותבת דבר",
  persists_the_review: "הבדיקה אינה נשמרת במסד",
  admits_a_skill_record: "הבדיקה אינה מכניסה רשומת מיומנות",
  installs_or_enables_a_skill: "הבדיקה אינה מתקינה ואינה מפעילה מיומנות",
  grants_execution_authority: "הבדיקה אינה מעניקה הרשאת ביצוע",
  grants_a_tool_or_capability_permission: "הבדיקה אינה מעניקה הרשאת כלי או יכולת",
  grants_a_customer_connection: "הבדיקה אינה מעניקה חיבור ללקוח",
  grants_an_approval_right: "הבדיקה אינה מעניקה זכות אישור",
  activates_schedules_or_runs_anything: "הבדיקה אינה מפעילה תזמון ואינה מריצה דבר",
};

// ─── The declaration fields the form collects ──────────────────────────────────
//
// Every field the server will read out of a declaration, split by the half it belongs to. The
// server publishes both halves (`declaration_fields.source` / `.attestation` /
// `.entry_from_review_request` / `.entry_from_discovery_record`), so the FORM is built from those
// lists; these are only their labels.

export const SKILL_REVIEW_FIELD_HE = {
  // the source layer
  source_form: "סוג המקור",
  source_id: "מזהה המקור",
  source_locator: "כתובת המקור",
  discovered_by: "מי גילה",
  source_revision: "מהדורת המקור (קומיט או תקציר תוכן)",
  discovered_at: "מועד הגילוי",
  // the attestation
  attested_by: "מי אישר את המקור",
  attested_at: "מועד האישור",
  expires_at: "האישור בתוקף עד",
  attested_source_id: "המזהה שהאישור מצמיד",
  attested_origin: "סוג המקור שהאישור מצמיד",
  attested_revision: "המהדורה שהאישור מצמיד",
  // the entry, as declared in this request
  domain: "תחום",
  subdomain: "תת-תחום",
  signature: "חתימת המפרסם",
  signer: "שם המפרסם",
  license: "רישיון",
  upstream_author: "המחבר במעלה הזרם",
  replaces_custom_code: "קוד מקומי שהמועמד מחליף",
  supply_chain_notes: "הערות שרשרת אספקה",
  compatibility: "תאימות",
  estimated_effort: "מאמץ מוערך",
  declared_tools: "כלים שהמועמד מצהיר עליהם",
  // the entry, taken from the discovery record
  entry_key: "מזהה הרשומה",
  name: "שם",
  description: "תיאור",
  expected_benefit: "תועלת צפויה",
  version_pin: "הצמדת גרסה",
};

// ─── Resolvers ─────────────────────────────────────────────────────────────────
//
// Every resolver NAMES a value it does not know instead of echoing it. A bare `MAP[value]` lookup
// also answers `constructor`, `toString` and `__proto__` with an inherited member, which would print
// a function's source into a Hebrew badge, so the lookup is guarded by `hasOwnProperty`.

function word(map, value) {
  if (typeof value !== "string" || !Object.prototype.hasOwnProperty.call(map, value)) {
    return SKILL_REVIEW_UNKNOWN_HE;
  }
  return map[value];
}

export function skillReviewStateLabel(value) {
  return word(SKILL_REVIEW_STATE_HE, value);
}

export function skillReviewVerdictLabel(value) {
  return word(SKILL_REVIEW_VERDICT_HE, value);
}

export function skillReviewStageLabel(value) {
  return word(SKILL_REVIEW_STAGE_HE, value);
}

export function skillReviewSourceFormLabel(value) {
  return word(SKILL_REVIEW_SOURCE_FORM_HE, value);
}

export function skillReviewReasonLabel(value) {
  return word(SKILL_REVIEW_REASON_HE, value);
}

export function skillReviewOutOfScopeLabel(value) {
  return word(SKILL_REVIEW_OUT_OF_SCOPE_HE, value);
}

export function skillReviewFieldLabel(value) {
  return word(SKILL_REVIEW_FIELD_HE, value);
}

export function skillReviewAuthorityLabel(value) {
  return word(SKILL_REVIEW_AUTHORITY_HE, value);
}

/** The server's own ordered reason list, worded. Input order is preserved: the first entry is §3's
 * highest-precedence finding and the card reads left to right, so sorting here would invert it. */
export function skillReviewReasonLabels(values) {
  if (!Array.isArray(values)) return [];
  return values.map(skillReviewReasonLabel);
}

/**
 * The declaration problems the server named, worded as `{ value, label }`.
 *
 * `declaration_problems` carries the server's own field names, and they come in TWO shapes: a bare
 * source-layer field (`source_id`) and an attestation binding named under a prefix
 * (`attestation.attested_revision`). Both are worded, so an operator told "the gate could not be
 * asked" can see which box to fill instead of re-reading the whole form — and `value` keeps the
 * server's exact token on screen next to the word, because the token is what the API and the logs
 * call the field.
 */
export function skillReviewProblemLabels(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => {
    const token = typeof value === "string" ? value : "";
    const bare = token.startsWith("attestation.") ? token.slice("attestation.".length) : token;
    const label = skillReviewFieldLabel(bare);
    return {
      value: token,
      // The raw token is always shown, so an unprefixed name the module has no word for is still
      // readable rather than displayed as "לא מזוהה" alone.
      label: label === SKILL_REVIEW_UNKNOWN_HE ? token : `${label} (${bare})`,
    };
  });
}

/**
 * The authority block as sentences.
 *
 * Only a key whose value is EXACTLY `false` is worded. A key that is absent, or that carries
 * anything else — `true`, `"false"`, `0` — is reported as unknown rather than silently dropped: a
 * card that hid an authority it did not recognise would be the one place where "the review grants
 * nothing" could stop being true without anything on screen changing.
 */
export function skillReviewAuthoritySentences(authority) {
  if (!authority || typeof authority !== "object") return [];
  const out = [];
  for (const key of Object.keys(SKILL_REVIEW_AUTHORITY_HE)) {
    const value = Object.prototype.hasOwnProperty.call(authority, key) ? authority[key] : undefined;
    if (value === false) out.push(SKILL_REVIEW_AUTHORITY_HE[key]);
    else out.push(`${key}: ${SKILL_REVIEW_UNKNOWN_HE}`);
  }
  return out;
}

/**
 * The stages the pipeline walked, as a sequence, so the card can show where a rejected candidate
 * stopped. `ORDER` is the server's `stages` list; a server stage this module has no word for is
 * still positioned, and still named — a stage dropped from the display would make a candidate look
 * like it stopped earlier than it did.
 */
export function skillReviewStageTrail(serverStages, reached) {
  const order = Array.isArray(serverStages) ? serverStages.filter((s) => typeof s === "string") : [];
  const at = order.indexOf(reached);
  return order.map((stage, index) => ({
    stage,
    label: skillReviewStageLabel(stage),
    reached: at >= 0 && index <= at,
    current: index === at,
  }));
}
